
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, Copy, Edit, Trash2 } from "lucide-react";
import { useProject } from "@/hooks/useProject";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreateTemplateDialog } from "./components/CreateTemplateDialog";
import { DuplicateTemplateDialog } from "./components/DuplicateTemplateDialog";
import { DeleteTemplateDialog } from "./components/DeleteTemplateDialog";
import { EditTemplateDialog } from "./components/EditTemplateDialog";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/ui/data-table";

export default function TemplateList() {
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const { projectClient, currentProject } = useProject();

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', currentProject],
    queryFn: async () => {
      if (!currentProject) return []
      
      const projectId = currentProject === '3m' ? 1 : 2;
      
      const { data: templatesData, error: templatesError } = await projectClient
        .from('report_templates_v2')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false })
      
      if (templatesError) throw templatesError
      
      const { data: formsData, error: formsError } = await projectClient
        .from('jotform_form_mappings')
        .select(`
          *,
          parent:parent_form_id(
            id,
            form_title,
            jotform_form_id
          )
        `)
        .eq('project_id', projectId)
      
      if (formsError) throw formsError
      
      const templatesWithForms = templatesData?.map(template => {
        const formMapping = formsData?.find(form => form.jotform_form_id === template.jotform_form_id)
        return {
          ...template,
          jotform_form_mapping: formMapping || null
        }
      })
      
      return templatesWithForms || []
    },
    enabled: !!currentProject,
  });

  const { data: templateStats } = useQuery({
    queryKey: ['template-stats', currentProject],
    queryFn: async () => {
      if (!currentProject || !templates) return {};
      
      const projectId = currentProject === '3m' ? 1 : 2;
      
      const [variablesData, chartsData, promptsData] = await Promise.all([
        projectClient.from('template_variable_mappings').select('template_id').eq('project_id', projectId),
        projectClient.from('template_charts').select('template_id').eq('project_id', projectId),
        projectClient.from('template_prompts').select('template_id').eq('project_id', projectId)
      ]);
      
      const stats: Record<number, { variables: number; charts: number; prompts: number }> = {};
      
      templates?.forEach(template => {
        stats[template.id] = {
          variables: variablesData.data?.filter(v => v.template_id === template.id).length || 0,
          charts: chartsData.data?.filter(c => c.template_id === template.id).length || 0,
          prompts: promptsData.data?.filter(p => p.template_id === template.id).length || 0,
        };
      });
      
      return stats;
    },
    enabled: !!currentProject && !!templates,
  });

  const handleDuplicate = (template: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTemplate(template);
    setDuplicateDialogOpen(true);
  };

  const handleEdit = (template: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTemplate(template);
    setEditDialogOpen(true);
  };

  const handleDelete = (template: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedTemplate(template);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <DataTable
        title="Plantillas"
        data={templates}
        isLoading={isLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Buscar plantillas por nombre o ID..."
        onCreateNew={() => setCreateDialogOpen(true)}
        createButtonLabel="Nueva Plantilla"
        emptyIcon={<FileText className="w-10 h-10 text-muted-foreground" />}
        emptyTitle="No hay plantillas todavía"
        emptyDescription="Comienza creando tu primera plantilla para generar reportes personalizados."
        filterData={(template, query) =>
          template.name?.toLowerCase().includes(query.toLowerCase()) ||
          template.id.toString().includes(query)
        }
        columns={[
          {
            header: "Nombre",
            render: (template) => (
              <span className="font-medium">
                {template.name || "Sin nombre"}
              </span>
            ),
          },
          {
            header: "Tipo",
            render: (template) =>
              template.jotform_form_mapping ? (
                <div className="flex flex-col gap-1">
                  <Badge
                    variant="secondary"
                    className={
                      template.jotform_form_mapping.is_referral_form
                        ? "bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200 w-fit"
                        : "bg-green-50 text-green-700 hover:bg-green-100 border-green-200 w-fit"
                    }
                  >
                    {template.jotform_form_mapping.is_referral_form ? "Referido" : "Principal"}
                  </Badge>
                  {template.jotform_form_mapping?.is_referral_form &&
                    template.jotform_form_mapping?.parent &&
                    Array.isArray(template.jotform_form_mapping.parent) &&
                    template.jotform_form_mapping.parent.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        → {template.jotform_form_mapping.parent[0].form_title || template.jotform_form_mapping.parent[0].jotform_form_id}
                      </span>
                    )}
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">-</span>
              ),
          },
          {
            header: "Acciones",
            className: "text-right",
            render: (template) => (
              <div
                className="flex items-center justify-end gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => handleEdit(template, e)}
                  title="Editar"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={(e) => handleDuplicate(template, e)}
                  title="Duplicar"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={(e) => handleDelete(template, e)}
                  title="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ),
          },
        ]}
        onRowClick={(template) => navigate(`/admin/templates/${template.id}`)}
      />


      <CreateTemplateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {selectedTemplate && (
        <>
          <EditTemplateDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            template={selectedTemplate}
          />
          
          <DuplicateTemplateDialog
            open={duplicateDialogOpen}
            onOpenChange={setDuplicateDialogOpen}
            template={selectedTemplate}
          />
          
          <DeleteTemplateDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            template={selectedTemplate}
          />
        </>
      )}
    </>
  );
}
